import { useQueryClient, useQuery } from '@tanstack/react-query'
import {
  fetchContentBundle,
  fetchDraftContentBundle,
  checkDraftContentVersion,
} from '@/api/contentApi'
import { saveContentBundle, getCachedContentBundle } from '@/db/viewerDb'
import { useAppStore } from '@/store/appStore'
import { useLanguagePromptPending } from '@/hooks/useLanguagePrompt'
import { preloadMediaAssets } from '@/services/mediaPreloader'
import type { GetContentBundleResponse } from '@/types/api'
import { toast } from 'react-toastify'
import i18n from '@/i18n/i18n'

let quotaToastShown = false

export function useContentBundle() {
  const { selectedLanguage, isOffline, showDraftContent } = useAppStore()
  const language = selectedLanguage ?? 'en'
  const queryClient = useQueryClient()
  // A user who still owes an answer to the first-run language prompt must not have a bundle
  // downloaded in a language they are about to change. The query starts the moment they continue.
  const languagePromptPending = useLanguagePromptPending()

  // Draft bundles use a separate query key so they never collide with the published cache
  // and switching the toggle always triggers a fresh fetch without touching the offline store.
  const isDraft = showDraftContent && !isOffline

  return useQuery<GetContentBundleResponse>({
    queryKey: isDraft ? ['content-bundle-draft', language] : ['content-bundle', language],
    queryFn: async () => {
      // ── Draft mode ────────────────────────────────────────────────────────
      // Draft preview is always live-only — no offline fallback, no IndexedDB persistence.
      // Before fetching the full bundle, do a lightweight hash check so we can return the
      // already-cached query data when the server-side draft cache hasn't changed.
      if (isDraft) {
        const versionResponse = await checkDraftContentVersion(language)

        // If we already have the bundle in React Query's cache and the hash matches,
        // return the existing data to avoid an unnecessary full-bundle round-trip.
        const existing = queryClient.getQueryData<GetContentBundleResponse>([
          'content-bundle-draft',
          language,
        ])
        if (existing && existing.contentHash === versionResponse.contentHash) {
          return existing
        }

        return fetchDraftContentBundle(language)
      }

      // ── Published mode ────────────────────────────────────────────────────
      // When already known-offline, skip the network entirely and go straight to the cache.
      if (isOffline) {
        const cached = await getCachedContentBundle(language)
        if (cached) return cached
        throw new Error('No cached content available for offline use.')
      }

      // When online (or connectivity hasn't been determined yet on cold-start),
      // try the network first. If it fails for any reason — including being truly
      // offline before the connectivity hook has fired — fall back to the cache so
      // the app is still usable from a completely closed/offline state.
      try {
        const bundle = await fetchContentBundle(language)
        // The offline cache write is best-effort: a rejected or blocked IndexedDB
        // must not fail the query and strand the app on the loading splash.
        await saveContentBundle(bundle).catch((err) =>
          console.warn('[ContentBundle] failed to cache bundle:', err)
        )

        // Fire-and-forget: cache the bundle's media in IndexedDB for offline use.
        const { setImageCacheProgress, clearImageCacheProgress } = useAppStore.getState()
        preloadMediaAssets(bundle, (p) => setImageCacheProgress(p))
          .then((result) => {
            clearImageCacheProgress()
            if (result.quotaExceeded && !quotaToastShown) {
              quotaToastShown = true
              toast.warning(i18n.t('media.storageQuotaExceeded'))
            }
          })
          .catch((err) => {
            console.warn('[MediaPreloader] failed:', err)
            clearImageCacheProgress()
          })

        return bundle
      } catch (networkError) {
        const cached = await getCachedContentBundle(language)
        if (cached) return cached
        throw networkError
      }
    },
    enabled: !languagePromptPending,
    staleTime: 5 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    retry: isOffline ? 0 : 2,
  })
}
